/* The Bridge (brightivity.dev/bridge): computers, pairing, commands to Brightivity Helper, live events,
 * uploads. Everything through the one API host (ADR-006). */
import { API_BASE } from '../config.js';
import { api, getSession } from './client.js';

export const listDevices = () => api('/bridge/devices');
export const updateDevice = (id, patch) => api(`/bridge/devices/${id}`, { method: 'PATCH', body: patch });
export const claimComputer = (deviceId, claim) => api('/bridge/devices/claim', { method: 'POST', body: { deviceId, claim } });
export const pairComputer = (code, name) => api('/bridge/pair/complete', { method: 'POST', body: { code: String(code).trim().toUpperCase(), ...(name ? { name } : {}) } });
export const sendCommand = (deviceId, type, payload = {}, imageKey, requireOnline) => api(`/bridge/devices/${deviceId}/commands`, { method: 'POST', body: { type, payload, ...(imageKey ? { imageKey } : {}), ...(requireOnline ? { requireOnline: true } : {}) } });
export const getCommand = (deviceId, cid) => api(`/bridge/devices/${deviceId}/commands/${cid}`);
export const cancelCommand = (deviceId, cid) => api(`/bridge/devices/${deviceId}/commands/${cid}/cancel`, { method: 'POST' });

export const helperDownloadUrl = (os) => `${API_BASE}/download/${os === 'mac' ? 'mac' : 'win'}`;
export const browserOs = () => (/Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? 'mac' : 'win');

/**
 * Live events for one computer: {t:'device'|'command'|'event', …}. Reconnects with backoff; if
 * the socket drops for good, callers can still read a command's state with getCommand().
 * Returns close().
 */
export function watchDevice(deviceId, onMessage, onState) {
  let ws = null;
  let closed = false;
  let tries = 0;
  let timer = null;
  const open = () => {
    const s = getSession();
    if (!s || !s.token || closed) return;
    const url = new URL(`${API_BASE}/bridge/devices/${deviceId}/watch`);
    url.protocol = url.protocol === 'http:' ? 'ws:' : 'wss:';
    url.searchParams.set('token', s.token);
    ws = new WebSocket(url);
    ws.onopen = () => { tries = 0; onState && onState('open'); };
    ws.onmessage = (m) => { try { onMessage(JSON.parse(m.data)); } catch { /* ignore */ } };
    ws.onclose = () => {
      if (closed) return;
      onState && onState('reconnecting');
      timer = setTimeout(open, Math.min(30000, 1000 * 2 ** Math.min(tries++, 5)));
    };
  };
  open();
  return () => { closed = true; clearTimeout(timer); if (ws) try { ws.close(); } catch { /* gone */ } };
}

/**
 * Run one command and resolve with its terminal event. onEvent gets every event (progress, log…).
 * Uses the watch socket when one is given, plus polling the command record as a safety net.
 */
export async function runCommand(deviceId, type, payload, { imageKey, onEvent, onQueued, timeoutMs = 120000, signal, requireOnline } = {}) {
  const r = await sendCommand(deviceId, type, payload, imageKey, requireOnline);
  if (!r.ok) return { ok: false, error: r.error, status: r.status };
  const cid = r.data.commandId;
  onQueued && onQueued(cid, r.data.online);
  const started = Date.now();
  let seen = 0;
  for (;;) {
    if (signal && signal.aborted) return { ok: false, error: 'aborted', commandId: cid };
    const c = await getCommand(deviceId, cid);
    if (c.ok && c.data && c.data.command) {
      const evs = c.data.command.events || [];
      for (; seen < evs.length; seen++) onEvent && onEvent(evs[seen]);
      const end = [...evs].reverse().find((e) => e.ev === 'result' || e.ev === 'error');
      if (end) return { ok: end.ev === 'result', commandId: cid, result: end, error: end.ev === 'error' ? (end.msg || end.error) : undefined, code: end.code };
    }
    if (timeoutMs && Date.now() - started > timeoutMs) return { ok: false, error: 'The computer did not answer in time.', commandId: cid, code: 'TIMEOUT' };
    await new Promise((res) => setTimeout(res, 800));
  }
}

const PART = 50 * 1024 * 1024;
/** Upload a File/Blob through brightivity.dev (direct ≤ 50 MB, else multipart). → {ok, key} */
export async function uploadFile(file, onProgress) {
  const s = getSession();
  const auth = { Authorization: `Bearer ${s && s.token}` };
  const type = file.type || 'image/jpeg';
  if (file.size <= PART) {
    const res = await fetch(`${API_BASE}/bridge/uploads/direct?contentType=${encodeURIComponent(type)}`, { method: 'PUT', headers: { ...auth, 'Content-Type': type }, body: file });
    const j = await res.json().catch(() => ({}));
    onProgress && onProgress(1);
    return res.ok && j.key ? { ok: true, key: j.key } : { ok: false, error: j.error || `Upload failed (${res.status})` };
  }
  const st = await api('/bridge/uploads/start', { method: 'POST', body: { contentType: type } });
  if (!st.ok) return { ok: false, error: st.error };
  const parts = [];
  const n = Math.ceil(file.size / PART);
  for (let i = 0; i < n; i++) {
    const res = await fetch(`${API_BASE}/bridge/uploads/part?key=${encodeURIComponent(st.data.key)}&uploadId=${encodeURIComponent(st.data.uploadId)}&part=${i + 1}`, { method: 'PUT', headers: auth, body: file.slice(i * PART, (i + 1) * PART) });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || !j.etag) return { ok: false, error: j.error || `Upload failed (${res.status})` };
    parts.push({ partNumber: j.partNumber, etag: j.etag });
    onProgress && onProgress((i + 1) / n);
  }
  const done = await api('/bridge/uploads/complete', { method: 'POST', body: { key: st.data.key, uploadId: st.data.uploadId, parts } });
  return done.ok ? { ok: true, key: done.data.key } : { ok: false, error: done.error };
}

/** Copy a Brightivity image (library design / printable) into the print-jobs bucket on the server. → {ok, key} */
export async function copyToJobs(sourceUrl) {
  const r = await api('/bridge/uploads/copy', { method: 'POST', body: { url: sourceUrl } });
  return r.ok && r.data && r.data.key ? { ok: true, key: r.data.key, size: r.data.size, contentType: r.data.contentType } : { ok: false, error: r.error };
}
